import { SingleInstanceClass } from "@src/core/utils/SingleInstanceClass";
import { IGameServerConnector } from "@src/server/IGameServerConnector";
import { LoadingScreen } from "@src/game/screens/LoadingScreen";
import { EventSystem } from "@src/core/events/EventSystem";
import { TGameUIButtonType } from "@src/game/ui/GameUI";
import { Connector } from "@src/server/Connector";
import {
  EGameState,
  TInitialResponse,
  TSpinResponse,
} from "@src/server/ResponseTypes";
import { constants } from "@src/game/Constants";
import { Audio } from "@src/core/audio/Audio";

interface IGameConfiguration {
  reels: number;
  rows: number;
  isCascade: boolean;
  winType: string;
}

// All the backend interactions happen through GameEngine.
// It listens to game events and updates its data accordingly.
export class GameEngine extends SingleInstanceClass {
  private _client!: IGameServerConnector;

  private _config: IGameConfiguration = {
    reels: 5,
    rows: 4,
    isCascade: false,
    winType: "line",
  };
  public get config(): IGameConfiguration {
    return this._config;
  }

  private _formatter!: Intl.NumberFormat;
  public get formatter(): Intl.NumberFormat {
    if (!this._formatter) {
      this._formatter = new Intl.NumberFormat("en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return this._formatter;
  }

  // Since our default bet is 1 and we don't store previous saved
  // bets in localstorage,we can safely use 1 as the default bet
  private _bet: number = 1;
  public get bet(): number {
    return this._bet;
  }
  public set bet(value: number) {
    this._bet = value;
  }

  // It is preferrable to use these getters to have
  // a single source of truth across the game.
  private _gameInitialData!: TInitialResponse;
  public get gameInitialData(): TInitialResponse {
    return this._gameInitialData;
  }
  private _gameActionData!: TSpinResponse;
  public get gameActionData(): TSpinResponse {
    return this._gameActionData;
  }

  private _autoplayCount: number | "∞" = -1;
  private _autoplaySelected: number | "∞" = -1;
  public get autoplayCount(): number | "∞" {
    return this._autoplayCount;
  }
  public resetAutoplay(): void {
    this._autoplayCount = -1;
    this._autoplaySelected = -1;

    EventSystem.invoke(constants.GameEvents.MODIFIED_AUTOPLAY_COUNT, this._autoplayCount);
  }
  public get isAutoplayActive(): boolean {
    return (typeof gameEngine.autoplayCount === "number" && gameEngine.autoplayCount > 0) || gameEngine.autoplayCount === "∞";
  }

  public async start(): Promise<void> {
    this._client = new Connector();
    this._setupEventListeners();

    const data = await this._client.requestInitialGameData();
    this._gameInitialData = data;
    this._bet = data.bet;

    console.log("Game initialized");
    console.log(this.gameInitialData);

    EventSystem.invoke(constants.GameEvents.GAME_INITIALIZE, data);
  }

  private async _requestGameAction(rigTitle?: string) {
    EventSystem.invoke(constants.GameEvents.GAME_REQUEST_ACTION);

    if (this.isAutoplayActive && (this._autoplaySelected === this._autoplayCount) && typeof this._autoplayCount === "number") {
      this._autoplayCount--;
      EventSystem.invoke(constants.GameEvents.MODIFIED_AUTOPLAY_COUNT, this._autoplayCount);
    }

    const data = await this._client.requestGameActionData(rigTitle);
    Audio.play("sndSpin");
    Audio.fadeOutToEnd("sndIntroduction");
    this._gameActionData = data;

    console.log("Performed game action");
    console.log(this.gameActionData);
    console.log(this.gameActionData.panelNew);

    EventSystem.invoke(constants.GameEvents.GAME_PERFORM_ACTION, data);
  }

  private async _onSelectedAutoplayCount(count: number | "∞"): Promise<void> {
    this._autoplayCount = count;
    this._autoplaySelected = count;
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.UIEvents.SELECTED_AUTOPLAY_COUNT,
      this._onSelectedAutoplayCount.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.BET_UPDATED,
      this._onBetUpdated.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._onReelPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.CELL_PANEL_SPIN_STOPPED,
      this._onCellPanelSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.WINNING_POPUP_CLOSED,
      this._onWinningPopupClosed.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._onUIButtonClicked.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._onRigButtonClicked.bind(this),
    );
  }

  private _onBetUpdated(bet: number): void {
    this._bet = bet;
  }

  private async _handleAutoplayNextStep() {
    if (!this.isAutoplayActive) return;

    if (typeof this._autoplayCount === "number") {
      if (this._autoplayCount > 0) {
        this._autoplayCount--;
        console.log("updated autoplayCount to " + this._autoplayCount);
        EventSystem.invoke(constants.GameEvents.MODIFIED_AUTOPLAY_COUNT, this._autoplayCount);
        await this._requestGameAction();
      } else {
        // Clean up: set to -1 so isAutoplayActive becomes false
        this.resetAutoplay();
      }
    } else {
      // Infinite case
      await this._requestGameAction();
    }
  }

  private async _onReelPanelSpinStopped(): Promise<void> {
    if (EGameState.MAIN !== this.gameActionData.state) {
      this._autoplayCount = 0;
    }

    switch (this.gameActionData.state) {
      case EGameState.BONUS:
        if (this.gameActionData.wins.length === 0) {
          // Perform automatic spins when we are in bonus game
          await this._requestGameAction();
        }
        break;
      case EGameState.MAIN:
        if (this.gameActionData.wins.length === 0) {
          await this._handleAutoplayNextStep();
        }
        break;
    }
  }

  private async _onCellPanelSpinStopped(): Promise<void> {
    switch (this.gameActionData.state) {
      case EGameState.HOLD:
        await this._requestGameAction();
        break;
    }
  }

  private async _onWinningPopupClosed(): Promise<void> {
    switch (this.gameActionData.state) {
      case EGameState.MAIN:
        await this._handleAutoplayNextStep();
        break;
      case EGameState.BONUS:
        // Perform automatic spins when we are in bonus game
        await this._requestGameAction();
        break;
    }
  }

  private async _onUIButtonClicked(
    buttonType: TGameUIButtonType,
  ): Promise<void> {
    switch (buttonType) {
      case "spin":
        if (this.isAutoplayActive && ((this._autoplaySelected !== this._autoplayCount) || this._autoplaySelected === "∞")) {
          this.resetAutoplay();

          return;
        }


        // case "action":
        if (!LoadingScreen.isLoaded) {
          await this._requestGameAction();
        }
    }
  }

  private async _onRigButtonClicked(rig: string): Promise<void> {
    await this._requestGameAction(rig);
  }
}

export const gameEngine = new GameEngine();
